import { useEffect, useState } from 'react';
import { Badge, Button, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import { Link, useSearchParams } from 'react-router-dom';
import { SUPPORT_EMAIL, TEST_OFFER_BADGE, formatRubles, type CheckoutView } from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { SESSION_QUERY_KEY } from '@/features/auth/useSession';
import { checkoutKey, fetchCheckout } from '@/features/admin/payments-api';
import { LAST_CHECKOUT_KEY } from './CheckoutProducts';

/**
 * Возврат из Robokassa (SuccessURL / FailURL магазина).
 *
 * Страница ничего не выдаёт и параметрам адреса не верит: из них берётся
 * только номер счёта, а состояние — с сервера, где его меняет уведомление
 * Robokassa (ResultURL). Пока уведомления нет — «проверяем платёж» и опрос.
 */

/** Сколько ждать уведомления, опрашивая сервер, прежде чем сказать «дольше обычного». */
const POLL_MS = 3000;
const PATIENCE_MS = 2 * 60 * 1000;

function checkoutIdFrom(params: URLSearchParams): string | null {
  const fromRobokassa = params.get('Shp_order');
  if (fromRobokassa) return fromRobokassa;
  try {
    return sessionStorage.getItem(LAST_CHECKOUT_KEY);
  } catch {
    return null;
  }
}

const date = (iso: string | null) =>
  // «2 ноября 2028», без «г.»: за датой в тексте идёт своя точка.
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).replace(/\s?г\.$/, '') : '';

export function PaymentReturnPage({ outcome }: { outcome: 'success' | 'fail' }) {
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const checkoutId = checkoutIdFrom(params);
  const [late, setLate] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLate(true), PATIENCE_MS);
    return () => clearTimeout(timer);
  }, []);
  const checkout = useQuery({
    queryKey: checkoutKey(checkoutId ?? ''),
    queryFn: () => fetchCheckout(checkoutId!),
    enabled: checkoutId !== null,
    staleTime: 0,
    refetchInterval: (query) => (waiting(query.state.data) && !late ? POLL_MS : false),
  });

  const settled = checkout.data?.order?.status;
  useEffect(() => {
    // Доступ выдан — кабинет должен увидеть новый тариф без перезагрузки.
    if (settled === 'granted') void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [settled, queryClient]);

  return (
    <>
      <PageHeader eyebrow="Оплата" title="Результат оплаты" />
      {checkoutId === null ? (
        <Notice tone="warning" title="Не нашли счёт">
          <Text size="sm">Если оплата прошла, доступ появится автоматически, а письмо придёт на почту.</Text>
        </Notice>
      ) : checkout.isLoading ? (
        <LoadingState label="Проверяем платёж..." />
      ) : checkout.isError || !checkout.data ? (
        <ErrorState onRetry={() => void checkout.refetch()} />
      ) : (
        <CheckoutState view={checkout.data} outcome={outcome} late={late} />
      )}
    </>
  );
}

const waiting = (view: CheckoutView | undefined) =>
  view !== undefined && (view.order === null || view.order.status === 'received' || view.order.status === 'verifying');

function CheckoutState({ view, outcome, late }: { view: CheckoutView; outcome: 'success' | 'fail'; late: boolean }) {
  const head = (
    <Group gap="xs">
      <Text fw={600}>{view.title}</Text>
      <Text c="var(--text-muted)">· {formatRubles(view.amountKopecks)} · счёт № {view.invoiceId}</Text>
      {view.test ? <Badge size="xs" variant="light" color="grape">{TEST_OFFER_BADGE}</Badge> : null}
    </Group>
  );
  const status = view.order?.status;

  if (status === 'granted') {
    return (
      <Notice tone="success" title="Оплата получена, доступ открыт">
        {head}
        <Text size="sm">{view.endsAt ? `Тариф действует до ${date(view.endsAt)}.` : 'Покупка уже в кабинете.'} Письмо с подтверждением — на почте.</Text>
        {view.recurring ? (
          <Text size="sm">
            Автопродление подключится в течение минуты: дата следующего списания и кнопка отключения — в профиле.
          </Text>
        ) : null}
        <Group><Button component={Link} to="/dashboard" size="xs">В кабинет</Button></Group>
      </Notice>
    );
  }
  if (status === 'test') {
    return (
      <Notice tone="success" title="Тестовая оплата прошла">
        {head}
        <Text size="sm">Уведомление Robokassa получено и проверено. Тестовая оплата доступ не выдаёт.</Text>
        <Group><Button component={Link} to="/dashboard" size="xs" variant="light">В кабинет</Button></Group>
      </Notice>
    );
  }
  if (status === 'needs_review' || status === 'awaiting_account' || status === 'refunded') {
    return (
      <Notice tone="warning" title="Оплата получена, проверяем вручную">
        {head}
        <Text size="sm">
          С заказом нужно разобраться человеку — мы уже получили уведомление и напишем на почту. Вопросы — {SUPPORT_EMAIL}.
        </Text>
      </Notice>
    );
  }
  if (outcome === 'fail' && view.order === null) {
    return (
      <Notice tone="warning" title="Платёж не прошёл или отменён">
        {head}
        <Text size="sm">Деньги не списаны. Попробовать ещё раз можно на странице тарифов.</Text>
        <Group><Button component={Link} to="/tariffs" size="xs">К тарифам</Button></Group>
      </Notice>
    );
  }
  return (
    <Notice tone="pending" title={late ? 'Проверка занимает дольше обычного' : 'Ждём подтверждения от Robokassa'}>
      {head}
      <Text size="sm">
        {late
          ? `Если оплата прошла, доступ появится автоматически и придёт письмо. Если этого не случится в течение часа — напишите на ${SUPPORT_EMAIL}, указав номер счёта.`
          : 'Обычно это занимает несколько секунд. Страницу можно не обновлять.'}
      </Text>
    </Notice>
  );
}

function Notice({
  tone,
  title,
  children,
}: {
  tone: 'success' | 'warning' | 'pending';
  title: string;
  children: React.ReactNode;
}) {
  const icon =
    tone === 'success' ? (
      <IconCircleCheck size={22} color="var(--mantine-color-teal-6)" />
    ) : tone === 'warning' ? (
      <IconAlertTriangle size={22} color="var(--mantine-color-yellow-7)" />
    ) : (
      <Loader size={20} />
    );
  return (
    <Paper withBorder p="md" radius="lg" maw={640} data-testid={`payment-${tone}`}>
      <Stack gap="xs">
        <Group gap="xs">
          {icon}
          <Title order={4}>{title}</Title>
        </Group>
        {children}
      </Stack>
    </Paper>
  );
}
